import React, { createContext, useContext, useEffect, useState } from 'react';

export const MOVE_TYPES = ['stepover', 'elastico', 'direction_change', 'feint', 'oriented_control', 'dribbling', 'other'];

const translations = {
  en: {
    appName: 'SkillSync AI',
    tagline: 'The Definitive Ecosystem for Competitive Football',
    loginHero: 'Train. Analyze. Dominate.',
    loginSub: 'Upload a 15s clip and get skeletal tracking, real biomechanical telemetry and pro-level AI coaching. Then share it on The Pitch and learn from real coaches.',
    benefit1: 'Real skeletal tracking overlay + measured knee angles & execution time',
    benefit2: 'The Pitch: share skills, get technical upvotes and advice',
    benefit3: 'Pro Academy: courses and personal video-reviews from real coaches',
    signInGoogle: 'Continue with Google',
    legalNote: 'By signing in you agree to our terms of service.',
    navDashboard: 'Home',
    navPitch: 'The Pitch',
    navAnalyze: 'Analyze',
    navAcademy: 'Academy',
    navLibrary: 'Library',
    skillRating: 'Skill Rating',
    avgOfAll: 'average of all analyses',
    totalAnalyses: 'Analyses',
    bestScore: 'Best',
    progressTitle: 'Progress',
    analyzeNewSkill: 'Analyze New Skill',
    recentAnalyses: 'Recent Analyses',
    communityHighlights: 'Community Highlights',
    viewAll: 'View all',
    noAnalysesTitle: 'No analyses yet',
    noAnalysesSub: 'Upload your first skill clip to unlock your Skill Rating.',
    trendNeedMore: 'Complete at least 2 analyses to unlock your progress trend.',
    uploadTitle: 'Upload your skill clip',
    uploadSub: 'Max 15 seconds. Frame your full body and the ball: approach, execution, exit.',
    chooseFromGallery: 'Choose from gallery',
    recordNow: 'Record now',
    moveTypeOptional: 'Move type (optional)',
    letAiDetect: 'Let AI detect',
    startAnalysis: 'Start Analysis',
    stepUploading: 'Uploading clip',
    stepTracking: 'Skeletal tracking (computer vision)',
    stepAnalyzing: 'AI analyzing biomechanics',
    stepGenerating: 'Generating report',
    processingHint: 'Usually takes 40\u201390 seconds. Keep this screen open.',
    analysisFailedTitle: 'Analysis failed',
    analysisFailedSub: 'Something went wrong while analyzing your clip.',
    tryAgain: 'Try again',
    videoTooLongClient: 'This video is {d}s long. Maximum allowed is 15 seconds.',
    invalidTypeClient: 'Please select a video file (mp4, mov, webm).',
    overallScore: 'Overall Score',
    executionSpeed: 'Execution Speed',
    movementFluidity: 'Movement Fluidity',
    centerOfGravity: 'Center of Gravity',
    ballControl: 'Ball Control',
    strengths: 'Strengths',
    suggestions: 'How to improve',
    aiConfidence: 'AI confidence',
    lowConfidenceNote: 'The AI could not clearly identify a football skill in this clip. Film your full body and the ball for best results.',
    playbackSpeed: 'Speed',
    originalVideo: 'Original',
    skeletonVideo: 'AI Tracking',
    telemetryTitle: 'Biomech Telemetry',
    telemetrySub: 'measured by computer vision',
    executionTime: 'Execution time',
    kneeLeft: 'Knee flexion L',
    kneeRight: 'Knee flexion R',
    cogVariation: 'CoG vertical shift',
    ballDistance: 'Ball-foot distance',
    aiEstimate: 'AI estimate',
    poseCoverage: 'Tracking coverage',
    notAvailable: 'n/a',
    shareOnPitch: 'Share on The Pitch',
    sharedOnPitch: 'On The Pitch',
    viewPost: 'View post',
    requestCoachReview: 'Coach Review \u20ac10',
    filterByMove: 'Filter by move',
    all: 'All',
    free: 'FREE',
    processing: 'Analyzing\u2026',
    failed: 'Failed',
    emptyLibraryTitle: 'Your library is empty',
    emptyLibrarySub: 'Every analyzed clip lands here so you can track your evolution.',
    emptyFilterTitle: 'No results for this move',
    deleteConfirmTitle: 'Delete this analysis?',
    deleteConfirmDesc: 'The video and its report will be removed from your library.',
    cancel: 'Cancel',
    confirmDelete: 'Delete',
    logout: 'Log out',
    notFoundTitle: 'Not found',
    backToDashboard: 'Back',
    points: 'pts',
    students: 'students',
    selectClip: 'Select clip',
    chooseMyClip: 'Choose one of your clips',
    noDoneAnalyses: 'No completed analyses yet. Analyze a clip first.',
    userLabel: 'User',
    productLabel: 'Product',
    amountLabel: 'Amount',
    statusLabel: 'Status',
    noTransactions: 'No transactions yet',
    adminStats: { users: 'Users', coaches: 'Coaches', analyses: 'Analyses', posts: 'Posts', courses: 'Courses', paid_transactions: 'Paid orders', pending_coach_requests: 'Pending applications' },
    profileSettings: 'Profile Settings',
    settingsSub: 'Manage your public identity on SkillSync',
    usernameLabel: 'Username',
    usernameHint: '3-20 characters: letters, numbers, dots, underscores',
    saveChanges: 'Save changes',
    profileUpdated: 'Profile updated',
    usernameTaken: 'Username already taken',
    changePhoto: 'Change photo',
    avatarUpdated: 'Photo updated',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    passwordHint: 'Minimum 6 characters',
    signIn: 'Sign in',
    signUp: 'Sign up',
    createAccount: 'Create account',
    orDivider: 'or',
    continueWithApple: 'Continue with Apple',
    comingSoon: 'Coming soon',
    authInvalidCredentials: 'Invalid email or password',
    authEmailExists: 'Email already registered',
    authUseGoogle: 'This account uses Google Sign-In',
    slowMotion: 'Slow-mo',
    // Pitch
    pitchTitle: 'The Pitch',
    pitchEmpty: 'No skills shared yet',
    pitchEmptySub: 'Be the first: analyze a clip and share it from the result page.',
    comments: 'Comments',
    writeComment: 'Give technical advice\u2026',
    send: 'Send',
    noComments: 'No comments yet. Drop the first technical tip.',
    upvote: 'Technical upvote',
    compare: 'Compare',
    compareTitle: 'Split-Screen Compare',
    comparePick: 'Pick a second clip to compare',
    compareWithMine: 'My analyses',
    compareWithFeed: 'From The Pitch',
    swap: 'Swap',
    playBoth: 'Play both',
    pauseBoth: 'Pause both',
    captionPlaceholder: 'Add a caption for The Pitch\u2026',
    share: 'Share',
    deletePost: 'Delete post',
    // Academy
    academyTitle: 'Pro Academy',
    academySub: 'Courses and video-reviews from real coaches',
    catalog: 'Catalog',
    myCourses: 'My Courses',
    myReviews: 'My Reviews',
    coachArea: 'Coach Studio',
    becomeCoach: 'Become a Coach',
    becomeCoachSub: 'Are you a coach, trainer or scout? Apply and get verified by our admins.',
    coachBio: 'Professional bio',
    coachBioPlaceholder: 'Experience, certifications, achievements\u2026 (min 10 chars)',
    specialties: 'Specialties',
    specialtiesPlaceholder: 'e.g. dribbling, 1v1, youth development',
    linksLabel: 'Links (optional)',
    submitApplication: 'Submit application',
    applicationPending: 'Your application is under review by the admins.',
    applicationRejected: 'Your previous application was rejected. You may submit a new one.',
    applicationApproved: 'You are a verified coach!',
    enroll: 'Enroll free',
    buyCourse: 'Buy course',
    enrolled: 'Enrolled',
    modules: 'modules',
    lessons: 'Lessons',
    lockedLesson: 'Enroll to unlock',
    courseBy: 'by',
    priceFree: 'Free',
    paymentsNotConfigured: 'Payments not configured yet',
    // Coach studio
    createCourse: 'Create course',
    editCourse: 'Edit course',
    courseTitle: 'Course title',
    courseDescription: 'Description',
    coursePrice: 'Price (EUR, 0 = free)',
    coverUrl: 'Cover image URL (optional)',
    saveCourse: 'Save course',
    addModule: 'Add module',
    moduleTitle: 'Lesson title',
    moduleDescription: 'Lesson description (optional)',
    moduleVideo: 'Lesson video (max 10 min, 100MB)',
    uploadModule: 'Upload lesson',
    publish: 'Publish',
    unpublish: 'Unpublish',
    published: 'Published',
    draft: 'Draft',
    reviewQueue: 'Review Queue',
    aiAssistant: 'AI Assistant',
    aiAssistantPlaceholder: 'e.g. Build me a 4-week elastico training plan for U17\u2026',
    aiPreeval: 'AI Pre-evaluation',
    playerNote: 'Player note',
    writeFeedback: 'Write your professional feedback\u2026',
    sendFeedback: 'Send feedback',
    reviewCompleted: 'Completed',
    reviewPaid: 'Ready for review',
    reviewPendingPayment: 'Awaiting payment',
    noReviews: 'No review requests yet',
    coachFeedback: 'Coach feedback',
    watchStudentClip: 'Watch student clip',
    payNow: 'Pay now',
    chooseCoach: 'Choose your coach',
    reviewNotePlaceholder: 'What should the coach focus on? (optional)',
    requestAndPay: 'Request & pay \u20ac10',
    noCoaches: 'No verified coaches available yet',
    // Admin
    adminTitle: 'Admin Panel',
    coachRequests: 'Coach Applications',
    transactions: 'Transactions',
    approve: 'Approve',
    reject: 'Reject',
    noPending: 'No pending applications',
    stats: 'Platform stats',
    // Payments
    paymentSuccess: 'Payment successful!',
    paymentProcessing: 'Verifying payment\u2026',
    paymentCancelled: 'Payment cancelled',
    paymentCancelledSub: 'No charge was made. You can retry whenever you want.',
    paymentError: 'Payment verification failed',
    goToCourse: 'Go to course',
    goToReviews: 'Go to my reviews',
    backToAcademy: 'Back to Academy',
    roleBadge: { player: 'Player', coach: 'Coach', admin: 'Admin' },
    moves: {
      stepover: 'Stepover',
      elastico: 'Elastico',
      direction_change: 'Direction Change',
      feint: 'Feint',
      oriented_control: 'Oriented Control',
      dribbling: 'Dribbling',
      other: 'Other',
    },
  },
  it: {
    appName: 'SkillSync AI',
    tagline: "L'Ecosistema Definitivo per il Calcio Competitivo",
    loginHero: 'Allenati. Analizza. Domina.',
    loginSub: 'Carica un clip da 15s e ottieni tracking scheletrico, telemetria biomeccanica reale e coaching AI di livello pro. Poi condividilo su The Pitch e impara da veri coach.',
    benefit1: 'Tracking scheletrico reale + angoli ginocchia e tempi misurati',
    benefit2: 'The Pitch: condividi skill, ricevi upvote tecnici e consigli',
    benefit3: 'Pro Academy: corsi e video-review personali da veri coach',
    signInGoogle: 'Continua con Google',
    legalNote: 'Accedendo accetti i nostri termini di servizio.',
    navDashboard: 'Home',
    navPitch: 'The Pitch',
    navAnalyze: 'Analizza',
    navAcademy: 'Academy',
    navLibrary: 'Libreria',
    skillRating: 'Skill Rating',
    avgOfAll: 'media di tutte le analisi',
    totalAnalyses: 'Analisi',
    bestScore: 'Migliore',
    progressTitle: 'Progressi',
    analyzeNewSkill: 'Analizza Nuova Skill',
    recentAnalyses: 'Analisi Recenti',
    communityHighlights: 'Highlights della Community',
    viewAll: 'Vedi tutte',
    noAnalysesTitle: 'Nessuna analisi',
    noAnalysesSub: 'Carica il tuo primo clip per sbloccare il tuo Skill Rating.',
    trendNeedMore: 'Completa almeno 2 analisi per sbloccare il trend dei progressi.',
    uploadTitle: 'Carica il tuo clip',
    uploadSub: 'Max 15 secondi. Inquadra corpo intero e pallone: preparazione, esecuzione, uscita.',
    chooseFromGallery: 'Scegli dalla galleria',
    recordNow: 'Registra ora',
    moveTypeOptional: 'Tipo di mossa (opzionale)',
    letAiDetect: "Lascia rilevare all'AI",
    startAnalysis: 'Avvia Analisi',
    stepUploading: 'Caricamento clip',
    stepTracking: 'Tracking scheletrico (computer vision)',
    stepAnalyzing: 'AI analizza la biomeccanica',
    stepGenerating: 'Generazione report',
    processingHint: 'Di solito richiede 40\u201390 secondi. Tieni aperta questa schermata.',
    analysisFailedTitle: 'Analisi fallita',
    analysisFailedSub: "Qualcosa \u00e8 andato storto durante l'analisi del clip.",
    tryAgain: 'Riprova',
    videoTooLongClient: 'Questo video dura {d}s. Il massimo consentito \u00e8 15 secondi.',
    invalidTypeClient: 'Seleziona un file video (mp4, mov, webm).',
    overallScore: 'Punteggio Totale',
    executionSpeed: 'Velocit\u00e0 di Esecuzione',
    movementFluidity: 'Fluidit\u00e0 del Movimento',
    centerOfGravity: 'Baricentro',
    ballControl: 'Controllo Palla',
    strengths: 'Punti di forza',
    suggestions: 'Come migliorare',
    aiConfidence: 'Confidenza AI',
    lowConfidenceNote: "L'AI non ha identificato chiaramente una skill calcistica. Riprendi corpo intero e pallone per risultati migliori.",
    playbackSpeed: 'Velocit\u00e0',
    originalVideo: 'Originale',
    skeletonVideo: 'AI Tracking',
    telemetryTitle: 'Telemetria Biomeccanica',
    telemetrySub: 'misurata con computer vision',
    executionTime: 'Tempo di esecuzione',
    kneeLeft: 'Flessione ginocchio SX',
    kneeRight: 'Flessione ginocchio DX',
    cogVariation: 'Escursione baricentro',
    ballDistance: 'Distanza palla-piede',
    aiEstimate: 'stima AI',
    poseCoverage: 'Copertura tracking',
    notAvailable: 'n/d',
    shareOnPitch: 'Condividi su The Pitch',
    sharedOnPitch: 'Su The Pitch',
    viewPost: 'Vedi post',
    requestCoachReview: 'Review Coach \u20ac10',
    filterByMove: 'Filtra per mossa',
    all: 'Tutte',
    free: 'GRATIS',
    processing: 'In analisi\u2026',
    failed: 'Fallita',
    emptyLibraryTitle: 'La tua libreria \u00e8 vuota',
    emptyLibrarySub: 'Ogni clip analizzato arriva qui per tracciare la tua evoluzione.',
    emptyFilterTitle: 'Nessun risultato per questa mossa',
    deleteConfirmTitle: 'Eliminare questa analisi?',
    deleteConfirmDesc: 'Il video e il suo report saranno rimossi dalla tua libreria.',
    cancel: 'Annulla',
    confirmDelete: 'Elimina',
    logout: 'Esci',
    notFoundTitle: 'Non trovato',
    backToDashboard: 'Indietro',
    points: 'pt',
    students: 'iscritti',
    selectClip: 'Seleziona clip',
    chooseMyClip: 'Scegli uno dei tuoi clip',
    noDoneAnalyses: 'Nessuna analisi completata. Prima analizza un clip.',
    userLabel: 'Utente',
    productLabel: 'Prodotto',
    amountLabel: 'Importo',
    statusLabel: 'Stato',
    noTransactions: 'Nessuna transazione',
    adminStats: { users: 'Utenti', coaches: 'Coach', analyses: 'Analisi', posts: 'Post', courses: 'Corsi', paid_transactions: 'Ordini pagati', pending_coach_requests: 'Candidature in attesa' },
    profileSettings: 'Impostazioni Profilo',
    settingsSub: 'Gestisci la tua identità pubblica su SkillSync',
    usernameLabel: 'Username',
    usernameHint: '3-20 caratteri: lettere, numeri, punti, underscore',
    saveChanges: 'Salva modifiche',
    profileUpdated: 'Profilo aggiornato',
    usernameTaken: 'Username già in uso',
    changePhoto: 'Cambia foto',
    avatarUpdated: 'Foto aggiornata',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    passwordHint: 'Minimo 6 caratteri',
    signIn: 'Accedi',
    signUp: 'Registrati',
    createAccount: 'Crea account',
    orDivider: 'oppure',
    continueWithApple: 'Continua con Apple',
    comingSoon: 'Prossimamente',
    authInvalidCredentials: 'Email o password non validi',
    authEmailExists: 'Email già registrata',
    authUseGoogle: "Questo account usa l'accesso Google",
    slowMotion: 'Slow-mo',
    pitchTitle: 'The Pitch',
    pitchEmpty: 'Nessuna skill condivisa',
    pitchEmptySub: 'Sii il primo: analizza un clip e condividilo dalla pagina risultati.',
    comments: 'Commenti',
    writeComment: 'Dai un consiglio tecnico\u2026',
    send: 'Invia',
    noComments: 'Nessun commento. Lascia il primo consiglio tecnico.',
    upvote: 'Upvote tecnico',
    compare: 'Confronta',
    compareTitle: 'Confronto Split-Screen',
    comparePick: 'Scegli un secondo clip da confrontare',
    compareWithMine: 'Le mie analisi',
    compareWithFeed: 'Da The Pitch',
    swap: 'Inverti',
    playBoth: 'Riproduci entrambi',
    pauseBoth: 'Pausa entrambi',
    captionPlaceholder: 'Aggiungi una didascalia per The Pitch\u2026',
    share: 'Condividi',
    deletePost: 'Elimina post',
    academyTitle: 'Pro Academy',
    academySub: 'Corsi e video-review da veri coach',
    catalog: 'Catalogo',
    myCourses: 'I Miei Corsi',
    myReviews: 'Le Mie Review',
    coachArea: 'Coach Studio',
    becomeCoach: 'Diventa Coach',
    becomeCoachSub: 'Sei un coach, preparatore o scout? Candidati e fatti verificare dagli admin.',
    coachBio: 'Bio professionale',
    coachBioPlaceholder: 'Esperienza, certificazioni, risultati\u2026 (min 10 caratteri)',
    specialties: 'Specialit\u00e0',
    specialtiesPlaceholder: 'es. dribbling, 1v1, settore giovanile',
    linksLabel: 'Link (opzionale)',
    submitApplication: 'Invia candidatura',
    applicationPending: 'La tua candidatura \u00e8 in revisione dagli admin.',
    applicationRejected: 'La candidatura precedente \u00e8 stata rifiutata. Puoi inviarne una nuova.',
    applicationApproved: 'Sei un coach verificato!',
    enroll: 'Iscriviti gratis',
    buyCourse: 'Acquista corso',
    enrolled: 'Iscritto',
    modules: 'moduli',
    lessons: 'Lezioni',
    lockedLesson: 'Iscriviti per sbloccare',
    courseBy: 'di',
    priceFree: 'Gratis',
    paymentsNotConfigured: 'Pagamenti non ancora configurati',
    createCourse: 'Crea corso',
    editCourse: 'Modifica corso',
    courseTitle: 'Titolo corso',
    courseDescription: 'Descrizione',
    coursePrice: 'Prezzo (EUR, 0 = gratis)',
    coverUrl: 'URL immagine copertina (opzionale)',
    saveCourse: 'Salva corso',
    addModule: 'Aggiungi modulo',
    moduleTitle: 'Titolo lezione',
    moduleDescription: 'Descrizione lezione (opzionale)',
    moduleVideo: 'Video lezione (max 10 min, 100MB)',
    uploadModule: 'Carica lezione',
    publish: 'Pubblica',
    unpublish: 'Ritira',
    published: 'Pubblicato',
    draft: 'Bozza',
    reviewQueue: 'Coda Review',
    aiAssistant: 'Assistente AI',
    aiAssistantPlaceholder: 'es. Crea un piano di 4 settimane per l\u2019elastico per U17\u2026',
    aiPreeval: 'Pre-valutazione AI',
    playerNote: 'Nota del giocatore',
    writeFeedback: 'Scrivi il tuo feedback professionale\u2026',
    sendFeedback: 'Invia feedback',
    reviewCompleted: 'Completata',
    reviewPaid: 'Pronta per la review',
    reviewPendingPayment: 'In attesa di pagamento',
    noReviews: 'Nessuna richiesta di review',
    coachFeedback: 'Feedback del coach',
    watchStudentClip: 'Guarda il clip',
    payNow: 'Paga ora',
    chooseCoach: 'Scegli il tuo coach',
    reviewNotePlaceholder: 'Su cosa deve concentrarsi il coach? (opzionale)',
    requestAndPay: 'Richiedi e paga \u20ac10',
    noCoaches: 'Nessun coach verificato disponibile',
    adminTitle: 'Pannello Admin',
    coachRequests: 'Candidature Coach',
    transactions: 'Transazioni',
    approve: 'Approva',
    reject: 'Rifiuta',
    noPending: 'Nessuna candidatura in sospeso',
    stats: 'Statistiche piattaforma',
    paymentSuccess: 'Pagamento riuscito!',
    paymentProcessing: 'Verifica del pagamento\u2026',
    paymentCancelled: 'Pagamento annullato',
    paymentCancelledSub: 'Nessun addebito effettuato. Puoi riprovare quando vuoi.',
    paymentError: 'Verifica del pagamento fallita',
    goToCourse: 'Vai al corso',
    goToReviews: 'Vai alle mie review',
    backToAcademy: "Torna all'Academy",
    roleBadge: { player: 'Player', coach: 'Coach', admin: 'Admin' },
    moves: {
      stepover: 'Doppio Passo',
      elastico: 'Elastico',
      direction_change: 'Cambio di Direzione',
      feint: 'Finta',
      oriented_control: 'Controllo Orientato',
      dribbling: 'Dribbling',
      other: 'Altro',
    },
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem('skillsync_lang') || 'en');

  const setLang = (l) => {
    localStorage.setItem('skillsync_lang', l);
    setLangState(l);
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key, vars) => {
    let value = translations[lang]?.[key] ?? translations.en[key] ?? key;
    if (typeof value === 'string' && vars) {
      Object.entries(vars).forEach(([k, v]) => {
        value = value.replace(`{${k}}`, v);
      });
    }
    return value;
  };

  const moveLabel = (moveType, report) => {
    if (report) {
      const label = lang === 'it' ? report.move_label_it : report.move_label_en;
      if (label) return label;
    }
    return translations[lang].moves[moveType] || translations.en.moves[moveType] || moveType || '\u2014';
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, moveLabel }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
